import { motion } from "framer-motion";
import { FOUNDING_CAP } from "@/config";

const EASE = [0.16, 1, 0.3, 1];

// Fixed seeded shuffle: which box lights up for seat N is random-looking but stable.
const RANK = (() => {
  const cells = Array.from({ length: FOUNDING_CAP }, (_, i) => i + 1);
  let seed = 20260917;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }
  const rank = {};
  cells.forEach((cell, idx) => {
    rank[cell] = idx + 1;
  });
  return rank;
})();

export default function FoundingGrid({ taken }) {
  const cells = Array.from({ length: FOUNDING_CAP }, (_, i) => i + 1);

  return (
    <section data-testid="founding-section" className="mx-auto max-w-6xl px-6 py-28 sm:py-36">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="text-center"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#171512]/50">
          the founding register
        </p>
        <h2 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#171512] sm:text-6xl">
          200 founding seats. each one <span className="font-serif italic">numbered.</span>
        </h2>
        <p
          data-testid="founding-grid-counter-text"
          className="mt-6 font-mono text-sm tracking-[0.18em] text-[#B9822E]"
        >
          {taken == null
            ? "READING THE REGISTER"
            : `${taken} OF ${FOUNDING_CAP} FOUNDING SEATS CLAIMED`}
        </p>
      </motion.div>

      <motion.div
        data-testid="founding-grid-container"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
        className="mx-auto mt-14 grid w-full max-w-[620px] grid-cols-[repeat(25,minmax(0,1fr))] gap-[3px]"
      >
        {cells.map((n) => {
          const filled = taken != null && RANK[n] <= taken;
          const label = `Founding Seat #${String(n).padStart(3, "0")}: ${filled ? "Reserved" : "Available"}`;
          return (
            <div
              key={n}
              title={label}
              aria-label={label}
              className={`aspect-square rounded-[2px] transition-transform duration-200 hover:scale-150 ${
                filled ? "bg-[#F5C7D4]" : "bg-[#EAE7DC]"
              }`}
            />
          );
        })}
      </motion.div>

      <p className="mx-auto mt-10 max-w-xl text-center text-sm leading-[1.75] text-[#171512]/55">
        Founding numbers are issued once, in order, and never reissued. When 200 people have claimed
        a seat, the register closes for good. The waitlist stays open.
      </p>
    </section>
  );
}
